import { useState, useCallback, useEffect, useMemo } from 'react';
import { ChipList, Chip } from '@shipengine/giger';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { ServiceItem } from '../ServiceItem.js';
import { getServiceListStyles, getLoadingStyles, getChipListStyles, getChipStyles, getSelectAndFiltersSectionStyles, getServiceCardStyles } from './ServiceListStyles.js';
import ServiceSort from '../ServiceSort.js';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { useFetchServices } from '../../hooks/useFetchServices.js';
import { ServiceChipFilter, ServiceSortingOptions, ServiceListEvents, ServiceListFilters } from '../../types/types.js';
import { getComputedChipFilters, getChipFilteredServices, getSidePanelFilteredServices } from '../../utils/Filters.js';
import { MetricsProvider } from '../MetricsProvider/MetricsProvider.js';
import { SidePanelFilters } from '../SidePanelFilters/SidePanelFilters.js';
import { buildSortOptionKey } from '../../utils/Icon.js';
import { useFiltersStructure } from '../../hooks/useFiltersStructure.js';

const ServiceList = _ref => {
  let {
    EmptyState,
    LoadingState,
    isLoadingStateComplete = true,
    ServiceDetails,
    cdnUrl,
    checkedSidePanelFilters,
    children,
    containerId,
    debugMode = false,
    from,
    onChangeChipFilter,
    onChangeSidePanelFilters,
    onDetailsRequest,
    onDropOffPointsRequest,
    onError,
    onFetchServices,
    onLoading,
    onSelectService,
    searchParams,
    showFilters,
    showVat = false,
    tracker,
    tagsFeatureEnabled = false,
    sortingFeatureEnabled = false,
    isAnonymousUser = false
  } = _ref;
  const sdk = useSdk();
  const {
    t
  } = useTranslation();
  const [services, setServices] = useState();
  const [chipFilters, setChipFilters] = useState();
  const [selectedChipFilter, setSelectedChipFilter] = useState(ServiceChipFilter.ALL);
  const [selectedSidePanelFilters, setSelectedSidePanelFilters] = useState(checkedSidePanelFilters);
  const [isLoading, setIsLoading] = useState({
    fetchingServices: false,
    fetchingpriceAnatomy: false
  });
  const [servicesPriceAnatomy, setServicesPriceAnatomy] = useState(undefined);
  const [sortOptionValue, setSortOptionValue] = useState(() => {
    const initialState = tagsFeatureEnabled ? {
      label: buildSortOptionKey(ServiceSortingOptions.TAGGED_SERVICES, t),
      value: ServiceSortingOptions.TAGGED_SERVICES
    } : {
      label: buildSortOptionKey(ServiceSortingOptions.BASE_PRICE, t),
      value: ServiceSortingOptions.BASE_PRICE
    };
    return initialState;
  });
  const handleFetchLoading = useCallback(isFetchLoading => {
    if (isFetchLoading != isLoading.fetchingpriceAnatomy) {
      onLoading === null || onLoading === void 0 ? void 0 : onLoading(ServiceListEvents.PRICES, isFetchLoading);
      setIsLoading(oldState => ({
        ...oldState,
        fetchingpriceAnatomy: isFetchLoading
      }));
    }
  }, [onLoading, isLoading.fetchingpriceAnatomy]);
  const saveFetchedServices = useCallback(services => {
    onFetchServices === null || onFetchServices === void 0 ? void 0 : onFetchServices(services);
    setServices(services);
  }, [onFetchServices]);
  const filterStructure = useFiltersStructure(services, debugMode);
  useFetchServices(searchParams, handleFetchLoading, saveFetchedServices, isAnonymousUser, onError, sortOptionValue === null || sortOptionValue === void 0 ? void 0 : sortOptionValue.value);
  useEffect(() => {
    if (!debugMode || !services) {
      return;
    }
    let mounted = true;
    const promise = sdk.v1.debug.getPrice({
      ...searchParams,
      filter_services: services === null || services === void 0 ? void 0 : services.map(service => service.id)
    });
    onLoading === null || onLoading === void 0 ? void 0 : onLoading(ServiceListEvents.PRICES, true);
    setIsLoading(oldState => ({
      ...oldState,
      fetchingpriceAnatomy: true
    }));
    promise.then(priceAnatomyItems => {
      mounted && setServicesPriceAnatomy(priceAnatomyItems === null || priceAnatomyItems === void 0 ? void 0 : priceAnatomyItems.reduce((savedItems, item) => {
        savedItems[item.serviceId] = item.price;
        return savedItems;
      }, {}));
    }).catch(e => {
      if (e.status && e.status < 500) {
        e.log();
      }
      onError === null || onError === void 0 ? void 0 : onError(ServiceListEvents.PRICES, e);
    }).finally(() => {
      if (!mounted) return;
      onLoading === null || onLoading === void 0 ? void 0 : onLoading(ServiceListEvents.PRICES, false);
      setIsLoading(oldState => ({
        ...oldState,
        fetchingpriceAnatomy: false
      }));
    });
    return () => {
      mounted = false;
      if (promise.isPending()) {
        promise.cancel();
      }
    };
  }, [debugMode, onError, onLoading, sdk.v1.debug, searchParams, services]);
  const servicesSortedByPrice = useMemo(() => {
    const servicesToShort = services !== null && services !== void 0 && services.length ? [...services] : [];
    return servicesToShort.sort((a, b) => {
      var _a$price, _b$price;
      if ((_a$price = a.price) !== null && _a$price !== void 0 && _a$price.totalPrice && (_b$price = b.price) !== null && _b$price !== void 0 && _b$price.totalPrice) {
        var _a$price2, _b$price2;
        return ((_a$price2 = a.price) === null || _a$price2 === void 0 ? void 0 : _a$price2.totalPrice) - ((_b$price2 = b.price) === null || _b$price2 === void 0 ? void 0 : _b$price2.totalPrice);
      }
      return 0;
    });
  }, [services]);
  useEffect(() => {
    const isFilterActive = filter => {
      return filter.length > 1;
    };
    if (!services || !showFilters) {
      return;
    }
    if (selectedSidePanelFilters && services) {
      const entries = Object.entries(selectedSidePanelFilters);
      const newEntries = entries.map(_ref2 => {
        let [key, value] = _ref2;
        return [key, isFilterActive(filterStructure[key]) ? value : {}];
      });
      const newSelectedFilters = Object.fromEntries(newEntries);
      setSelectedSidePanelFilters(newSelectedFilters);
    }
    if (showFilters === ServiceListFilters.CHIPS) {
      setChipFilters(getComputedChipFilters(services));
    }
  }, [services, showFilters]);
  const filteredServices = useMemo(() => {
    if (!services) {
      return undefined;
    }
    if (showFilters === ServiceListFilters.CHIPS) {
      return getChipFilteredServices(services, selectedChipFilter);
    } else if (showFilters === ServiceListFilters.SIDEPANEL && selectedSidePanelFilters) {
      return getSidePanelFilteredServices(services, selectedSidePanelFilters);
    } else {
      return services;
    }
  }, [selectedChipFilter, selectedSidePanelFilters, services, showFilters]);
  const changeChipFilter = filter => () => {
    setSelectedChipFilter(filter);
    onChangeChipFilter === null || onChangeChipFilter === void 0 ? void 0 : onChangeChipFilter(filter);
  };
  const changeSidePanelFilters = filters => {
    setSelectedSidePanelFilters(filters);
    onChangeSidePanelFilters === null || onChangeSidePanelFilters === void 0 ? void 0 : onChangeSidePanelFilters(filters);
  };
  const handleOnSelectService = (service, index) => {
    const priceBasedPosition = servicesSortedByPrice.findIndex(element => element.id === service.id) + 1;
    onSelectService(service, index, priceBasedPosition);
  };
  const isLoadingStateActive = !isLoadingStateComplete || isLoading.fetchingServices || isLoading.fetchingpriceAnatomy;
  return jsx(MetricsProvider, {
    tracker: tracker,
    children: jsxs("section", {
      css: getServiceListStyles,
      children: [isLoadingStateActive && LoadingState && jsx("div", {
        css: getLoadingStyles,
        children: typeof LoadingState === 'function' ? jsx(LoadingState, {}) : LoadingState
      }), !isLoadingStateActive && jsxs(Fragment, {
        children: [showFilters === ServiceListFilters.CHIPS && chipFilters && jsx(ChipList, {
          css: getChipListStyles,
          children: chipFilters.map(filter => jsx(Chip, {
            css: getChipStyles,
            onClick: changeChipFilter(filter),
            isSelected: selectedChipFilter === filter,
            children: t(`service-list.filter.${filter}`)
          }, filter))
        }), jsxs("div", {
          css: getSelectAndFiltersSectionStyles(showFilters === ServiceListFilters.SIDEPANEL, sortingFeatureEnabled),
          children: [sortingFeatureEnabled && jsx(ServiceSort, {
            setSortOption: setSortOptionValue,
            sortOption: sortOptionValue,
            tagsFeatureEnabled: tagsFeatureEnabled
          }), showFilters === ServiceListFilters.SIDEPANEL && services && jsx(SidePanelFilters, {
            containerId: containerId,
            services: services,
            selectedFilters: selectedSidePanelFilters,
            onChangeFilters: changeSidePanelFilters,
            debugMode: debugMode
          })]
        }), filteredServices === null || filteredServices === void 0 ? void 0 : filteredServices.map((service, index) => jsx(ServiceItem, {
          index: index,
          service: service,
          from: from,
          ServiceDetails: ServiceDetails,
          cdnUrl: cdnUrl,
          onSelect: handleOnSelectService,
          onDetailsRequest: onDetailsRequest,
          onDropOffPointsRequest: onDropOffPointsRequest,
          onError: onError,
          onLoading: onLoading,
          priceAnatomy: servicesPriceAnatomy === null || servicesPriceAnatomy === void 0 ? void 0 : servicesPriceAnatomy[service.id],
          showVat: showVat,
          css: getServiceCardStyles,
          tagsFeatureEnabled: tagsFeatureEnabled,
          children: children
        }, `${service.carrierName}-${index}`)), (!filteredServices || filteredServices.length === 0) && EmptyState && jsx(EmptyState, {})]
      })]
    })
  });
};

export { ServiceList };
//# sourceMappingURL=ServiceList.js.map
